import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

export interface ComboboxOption {
  value: string;
  label: string;
  description?: string;
}

interface ComboboxProps {
  options: ComboboxOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  className?: string;
  /** Allow typing a custom value not in the options list */
  allowFreeText?: boolean;
  /** Loading state while options are being fetched */
  loading?: boolean;
  /** Error class on the trigger button */
  error?: boolean;
  id?: string;
  /**
   * Wrap long option labels instead of letting them stay on one line.
   * Useful for long opaque IDs whose tail (e.g. a trailing hash) must stay
   * readable. Scoped per-instance so other comboboxes keep their layout.
   */
  wrapOptionLabel?: boolean;
}

/**
 * Searchable combobox with optional free-text input.
 * Uses Popover + Command (cmdk) — the standard shadcn pattern.
 */
export function Combobox({
  options,
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  emptyText,
  disabled = false,
  className,
  allowFreeText = false,
  loading = false,
  error = false,
  id,
  wrapOptionLabel = false,
}: ComboboxProps) {
  const { t } = useTranslation('common');
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');

  // i18n defaults: resolved here (not as default-prop literals) so the prop
  // signature stays optional/byte-stable while the label follows the language.
  const resolvedPlaceholder = placeholder ?? t('combobox.select');

  const selectedOption = options.find((opt) => opt.value === value);
  const displayLabel = selectedOption?.label ?? (value || undefined);

  const handleSelect = (selectedValue: string) => {
    onChange(selectedValue === value ? '' : selectedValue);
    setOpen(false);
    setSearch('');
  };

  const handleFreeTextConfirm = () => {
    if (allowFreeText && search.trim()) {
      onChange(search.trim());
      setOpen(false);
      setSearch('');
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled || loading}
          className={cn(
            'w-full justify-between font-normal',
            !value && 'text-muted-foreground',
            error && 'border-destructive',
            className,
          )}
        >
          <span className="truncate">
            {loading ? t('combobox.loading') : (displayLabel ?? resolvedPlaceholder)}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter>
          <CommandInput
            placeholder={searchPlaceholder ?? t('combobox.search')}
            value={search}
            onValueChange={setSearch}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && allowFreeText && search.trim()) {
                // If no matching option is highlighted, accept free text
                const hasExactMatch = options.some(
                  (opt) => opt.value.toLowerCase() === search.trim().toLowerCase()
                    || opt.label.toLowerCase() === search.trim().toLowerCase()
                );
                if (!hasExactMatch) {
                  e.preventDefault();
                  handleFreeTextConfirm();
                }
              }
            }}
          />
          <CommandList>
            <CommandEmpty>
              {allowFreeText && search.trim() ? (
                <button
                  type="button"
                  className="w-full cursor-pointer touch-manipulation px-2 py-1.5 text-left text-sm hover:bg-accent coarse:min-h-10"
                  onClick={handleFreeTextConfirm}
                >
                  {t('combobox.useFreeText', { value: search.trim() })}
                </button>
              ) : (
                emptyText ?? t('combobox.empty')
              )}
            </CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  // Search matches label, value AND description so secondary
                  // identifiers (e.g. a customer number shown below the name)
                  // are findable too.
                  value={`${option.label} ${option.value} ${option.description ?? ''}`}
                  onSelect={() => handleSelect(option.value)}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === option.value ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <div className="flex min-w-0 flex-col">
                    <span
                      className={cn(
                        wrapOptionLabel && 'whitespace-normal break-all font-mono',
                      )}
                    >
                      {option.label}
                    </span>
                    {option.description && (
                      <span className="text-xs text-muted-foreground">{option.description}</span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
